import { useState, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { Video } from 'lucide-react';

export default function SignUp() {
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [fullName, setFullName] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [inviteInfo, setInviteInfo] = useState<any>(null);
  const { signUp } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const emailParam = searchParams.get('email');
    const inviteToken = searchParams.get('inviteToken');
    const eventId = searchParams.get('eventId');

    if (emailParam) {
      setEmail(emailParam);
    }

    if (inviteToken && eventId) {
      loadInviteInfo(inviteToken, eventId);
    }
  }, [searchParams]);

  const loadInviteInfo = async (token: string, eventId: string) => {
    try {
      const { data: invitation } = await supabase
        .from('event_invitations')
        .select('email, role, event_id')
        .eq('token', token)
        .eq('event_id', eventId)
        .eq('status', 'pending')
        .maybeSingle();

      if (invitation) {
        setInviteInfo(invitation);
        setEmail(invitation.email);
      }
    } catch (err) {
      console.error('Error loading invitation:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    const inviteToken = searchParams.get('inviteToken');
    const eventId = searchParams.get('eventId');

    const { error: signUpError, data } = await signUp(email, password, fullName, 'crew');

    if (signUpError) {
      if (signUpError.message.includes('already registered') || signUpError.message.includes('User already registered')) {
        setError('This email was already invited. Please check your email for the invitation link or contact an administrator.');
      } else {
        setError(signUpError.message);
      }
      setLoading(false);
      return;
    }

    if (inviteToken && eventId && data?.user) {
      try {
        const { error: accessError } = await supabase
          .from('event_access')
          .insert({
            event_id: eventId,
            user_id: data.user.id,
            role: inviteInfo?.role || 'viewer',
          });

        if (accessError && !accessError.message?.includes('duplicate')) {
          console.error('Error granting access:', accessError);
        }

        const { error: updateError } = await supabase
          .from('event_invitations')
          .update({ status: 'accepted' })
          .eq('token', inviteToken)
          .eq('event_id', eventId);

        if (updateError) {
          console.error('Error updating invitation:', updateError);
        }

        setLoading(false);
        navigate(`/portal?eventId=${eventId}`);
      } catch (err) {
        console.error('Error processing invitation:', err);
        setLoading(false);
        navigate('/confirm-email', { state: { email } });
      }
    } else {
      setLoading(false);
      navigate('/confirm-email', { state: { email } });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#1a1a1a] via-[#0a0a0a] to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        <div className="text-center mb-8">
          <div className="flex justify-center mb-6">
            <img
              src="/slidepusher_events.svg"
              alt="Slidepusher Events"
              className="h-20"
            />
          </div>
          <h1 className="text-2xl font-bold text-white mb-2">
            {inviteInfo ? 'Accept Event Invitation' : 'Create Account'}
          </h1>
          <p className="text-zinc-400">
            {inviteInfo ? 'Complete your account to access the event' : 'Join the event production portal'}
          </p>
        </div>

        <div className="bg-[#1a1a1a] rounded shadow-xl p-8 border border-zinc-800">
          <form onSubmit={handleSubmit} className="space-y-6">
            {error && (
              <div className="bg-red-500/10 border border-red-500/50 rounded p-3 text-red-400 text-sm">
                {error}
              </div>
            )}

            <div>
              <label htmlFor="fullName" className="block text-sm font-medium text-zinc-300 mb-2">
                Full Name
              </label>
              <input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="John Doe"
                required
              />
            </div>

            <div>
              <label htmlFor="email" className="block text-sm font-medium text-zinc-300 mb-2">
                Email
              </label>
              <input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="you@example.com"
                required
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-sm font-medium text-zinc-300 mb-2">
                Password
              </label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-zinc-900 border border-zinc-800 rounded text-white placeholder-zinc-500 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                placeholder="••••••••"
                required
                minLength={6}
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary-500 hover:bg-primary-600 text-white font-medium py-3 px-4 rounded transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? 'Creating account...' : 'Sign Up'}
            </button>
          </form>

          <div className="mt-6 space-y-3">
            <div className="bg-primary-500/10 border border-primary-500/50 rounded p-3">
              <p className="text-primary-500 text-xs text-center">
                <strong>Note:</strong> If you were invited by an admin, please use the invitation link from your email instead of signing up here.
              </p>
            </div>
            <div className="bg-yellow-500/10 border border-yellow-500/50 rounded p-3">
              <p className="text-yellow-400 text-xs text-center">
                Self-signup accounts require email confirmation before you can sign in.
              </p>
            </div>
            <p className="text-sm text-zinc-400 text-center">
              Already have an account?{' '}
              <Link to="/login" className="text-primary-500 hover:text-primary-400 font-medium">
                Sign in
              </Link>
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
